import { Stack } from "@mui/material";
import { useMemo, type FC } from "react";
import { useTypedTranslation } from "../../hooks";
import { Downloads, type DownloadProps } from "../Downloads";
import { FormErrorMessage } from "../FormErrorMessage";
import { BodyTextM, HeadingL } from "../Typography";
import * as Styled from "./DocumentsList.styled";

export type DocumentsListItem = DownloadProps & {
    id?: string | number;
    dateCreated?: Date | string;
};

export interface DocumentsListProps {
    documents?: DocumentsListItem[];
    isLoading?: boolean;
    isError?: boolean;
    errorCode?: number | string;
    showHeading?: boolean;
}

export const DocumentsList: FC<DocumentsListProps> = ({
    documents = [],
    isLoading = false,
    isError = false,
    errorCode,
    showHeading = true,
}) => {
    const { t } = useTypedTranslation();

    const content = useMemo(() => {
        if (!documents.length) {
            return (
                <Styled.EmptyStateWrapper>
                    <BodyTextM>{t("documents.empty.description")}</BodyTextM>
                </Styled.EmptyStateWrapper>
            );
        }

        let currentYear: number | undefined;

        const sortedDocuments = [...documents].sort((a, b) => {
            const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
            const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
            return dateB - dateA;
        });

        return sortedDocuments.map((document, index) => {
            const parsedDate = document.dateCreated ? new Date(document.dateCreated) : undefined;
            const year = parsedDate?.getFullYear();

            // Show year heading only if it's different from the previous document's year
            const shouldShowYear = typeof year === "number" && year !== currentYear;
            if (shouldShowYear) {
                currentYear = year;
            }

            const key = document.id ?? `${document.name}-${index}`;

            return (
                <Stack key={key}>
                    {shouldShowYear && <Styled.YearHeading $withTopPadding={index !== 0}>{year}</Styled.YearHeading>}
                    <Downloads
                        name={document.name}
                        dateCreated={parsedDate}
                        fileSize={document.fileSize}
                        mimeType={document.mimeType}
                        downloadPath={document.downloadPath}
                        isExternalUrl={document.isExternalUrl}
                    />
                </Stack>
            );
        });
    }, [documents, t]);

    const errorContent = useMemo(() => {
        const errorMessage = errorCode === 900 ? t("errors.documentsNotAvailable") : t("errors.general");
        return (
            <Styled.ErrorWrapper>
                <FormErrorMessage severity="error">
                    <BodyTextM>{t("errors.somethingWentWrong")}</BodyTextM>
                    <br />
                    <BodyTextM>{errorMessage}</BodyTextM>
                </FormErrorMessage>
            </Styled.ErrorWrapper>
        );
    }, [errorCode, t]);

    const loadingContent = useMemo(() => {
        return (
            <Styled.HalfWidthStack>
                {Array.from({ length: 3 }, (_, index) => (
                    <Styled.DocumentSkeletonRow key={index}>
                        <Styled.DocumentSkeletonIcon variant="rectangular" />
                        <Styled.DocumentSkeletonTextContainer>
                            <Styled.DocumentSkeletonLine />
                            <Styled.DocumentSkeletonLine />
                        </Styled.DocumentSkeletonTextContainer>
                    </Styled.DocumentSkeletonRow>
                ))}
            </Styled.HalfWidthStack>
        );
    }, []);

    return (
        <Styled.DocumentsListContainer>
            <Stack>
                {showHeading && (
                    <Styled.DocumentsListHeaderRow>
                        <HeadingL withoutScale>{t("documents.list.heading")}</HeadingL>
                    </Styled.DocumentsListHeaderRow>
                )}
                <Styled.DocumentsListItems>
                    {isLoading && loadingContent}
                    {isError && errorContent}
                    {!isLoading && !isError && content}
                </Styled.DocumentsListItems>
            </Stack>
        </Styled.DocumentsListContainer>
    );
};
